import { localIsoDate } from '@bariendo/lib/benefit-events';
import { PriorAuthRequirement } from '@bariendo/lib/constants';
import { MARKET_LOCATION_EXTENSION } from '@bariendo/lib/extensions';
import { readMarketLocation } from '@bariendo/lib/intake-selection';
import { appointmentHasOccurred } from '@bariendo/lib/pac-list-appointments';
import { ActionIcon, Button, FileButton, Group, Select, SimpleGrid, Stack, Text, Textarea } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { createReference } from '@medplum/core';
import { DocumentReference, Practitioner, Reference } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { IconCloudUpload, IconCreditCard, IconTrash } from '@tabler/icons-react';
import { ReactElement, ReactNode, useMemo, useState } from 'react';
import { AttachmentRow } from '@/components/Timeline/AttachmentRow';
import { BillingComposerCard } from './BillingComposerCard';
import { useCoverageActivityConfig } from '@/hooks/useCoverageActivityConfig';
import { useMarketOptions } from '@/hooks/useMarketOptions';
import { useTimelineContext } from '@/hooks/useTimelineContext';
import { alert } from '@/services/alert';
import {
  isAllowedUploadDocumentType,
  MAX_UPLOAD_DOCUMENT_SIZE_BYTES,
  uploadDocumentToPatient,
} from '@/services/documents';
import { fetchBot } from '@/services/services';

/** Files per card (design-system `timeline-employer-card.mdx`). */
export const MAX_COVERAGE_FILES = 5;

export interface CoverageActivityValues {
  action: string;
  /** YYYY-MM-DD, the day it happened. */
  date: string;
  location?: string;
  note?: string;
  /** The uploaded files, for the track to link to its case record. */
  documents: Reference<DocumentReference>[];
}

/** What a track gives the composer (BAR-3075): employer in 2.3, insurance in 3.2. */
export interface CoverageActivityConfig {
  actions: { value: string; label: string }[];
  /** The track's own fields, first in the grid: Payor and Employer, or Insurer and IDs. */
  trackFields?: ReactNode;
  /** False while the track's own fields are incomplete; Record waits for it. */
  ready?: boolean;
  /** The `DocumentReference.type` an action's files get, if any. */
  documentType?: (action: string) => PriorAuthRequirement | undefined;
  onRecord: (values: CoverageActivityValues) => Promise<void>;
}

/** The composer in the timeline's message box slot; nothing until the patient's track has one. */
export function CoverageActivityMessageBox(): ReactElement | null {
  const config = useCoverageActivityConfig();
  return config ? <CoverageActivityBox config={config} /> : null;
}

/**
 * + Add ▸ Coverage activity (BAR-3075): one inline card for every coverage step, on either
 * track. Uploads the files and saves a changed procedure location; the track records the step.
 */
export function CoverageActivityBox({ config }: { config: CoverageActivityConfig }): ReactElement | null {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const { patientContext, messageBoxContext, appointmentsContext, documentsContext } = useTimelineContext();
  const patient = patientContext?.patient;
  const { setShowMessageInput } = messageBoxContext;
  const { markets } = useMarketOptions();

  const savedLocation = useMemo(() => readMarketLocation(patient, undefined), [patient]);
  // Like the panel: the procedure's location is settled once it has happened.
  const locationLocked = (appointmentsContext?.procedures ?? []).some((appointment) =>
    appointmentHasOccurred(appointment, new Date())
  );

  const [action, setAction] = useState<string | null>(null);
  const [date, setDate] = useState<string | null>(localIsoDate());
  const [location, setLocation] = useState<string | null>(savedLocation ?? null);
  const [files, setFiles] = useState<File[]>([]);
  const [note, setNote] = useState('');
  const [recording, setRecording] = useState(false);

  if (!patient) {
    return null;
  }

  const addFiles = (selected: File[]): void => {
    if (selected.some((file) => !isAllowedUploadDocumentType(file))) {
      alert.error({ title: 'Unsupported file type', message: 'Only PDF and image files are supported.' });
      return;
    }
    if (selected.some((file) => file.size > MAX_UPLOAD_DOCUMENT_SIZE_BYTES)) {
      alert.error({ title: 'File too large', message: 'Each file must be 10MB or smaller.' });
      return;
    }
    if (files.length + selected.length > MAX_COVERAGE_FILES) {
      alert.error({ title: 'Too many files', message: `Up to ${MAX_COVERAGE_FILES} files per card.` });
      return;
    }
    setFiles((current) => [...current, ...selected]);
  };

  const record = async (): Promise<void> => {
    if (!action || !date) {
      return;
    }
    setRecording(true);
    try {
      const documents =
        files.length > 0 && profile
          ? await uploadDocumentToPatient(medplum, patient, profile, files, { type: config.documentType?.(action) })
          : [];
      if (!locationLocked && location && location !== savedLocation) {
        await fetchBot(medplum, 'set-patient-intake-selection', {
          patientId: patient.id,
          url: MARKET_LOCATION_EXTENSION,
          value: location,
        });
      }
      await config.onRecord({
        action,
        date,
        location: location ?? undefined,
        note: note.trim() || undefined,
        documents: documents.map((document) => createReference(document)),
      });
      setShowMessageInput(false);
      documentsContext?.refreshDocuments();
      await patientContext?.refreshPatient();
    } catch (error) {
      alert.error({ title: "Couldn't record that", message: error instanceof Error ? error.message : undefined });
    } finally {
      setRecording(false);
    }
  };

  return (
    <BillingComposerCard
      icon={<IconCreditCard size={16} />}
      title="Coverage activity"
      onClose={() => setShowMessageInput(false)}
    >
      <Stack p="md" gap="md">
        <SimpleGrid cols={2} spacing="md" verticalSpacing="sm">
          {config.trackFields}
          <Select
            label="Action"
            placeholder="Select an action"
            data={config.actions}
            value={action}
            onChange={setAction}
            disabled={recording}
          />
          <DateInput label="Date" value={date} onChange={setDate} maxDate={localIsoDate()} disabled={recording} />
          <Select
            label="Procedure location"
            data={markets}
            value={location}
            onChange={setLocation}
            disabled={recording || locationLocked}
          />
        </SimpleGrid>

        <Stack gap={6}>
          <Group justify="space-between">
            <Text fz="sm" fw={500}>
              Files
            </Text>
            <FileButton onChange={addFiles} multiple accept="application/pdf,image/*">
              {(props) => (
                <Button
                  {...props}
                  variant="subtle"
                  color="#375462"
                  rightSection={<IconCloudUpload size={16} />}
                  disabled={recording || files.length >= MAX_COVERAGE_FILES}
                >
                  Add files
                </Button>
              )}
            </FileButton>
          </Group>
          {files.map((file, index) => (
            <AttachmentRow
              key={`${file.name}-${index}`}
              variant="compose"
              name={file.name}
              size={file.size}
              actions={
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label={`Remove ${file.name}`}
                  onClick={() => setFiles((current) => current.filter((_, i) => i !== index))}
                  disabled={recording}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              }
            />
          ))}
          <Text fz="xs" c="dimmed">
            {files.length} of {MAX_COVERAGE_FILES}
          </Text>
        </Stack>

        <Textarea
          label="Note"
          placeholder="Optional"
          autosize
          minRows={2}
          value={note}
          onChange={(event) => setNote(event.currentTarget.value)}
          disabled={recording}
        />

        <Group justify="flex-end">
          <Button variant="subtle" color="gray" onClick={() => setShowMessageInput(false)} disabled={recording}>
            Cancel
          </Button>
          <Button
            onClick={() => void record()}
            disabled={!action || !date || config.ready === false}
            loading={recording}
          >
            Record
          </Button>
        </Group>
      </Stack>
    </BillingComposerCard>
  );
}
